/**
 * Brand loader: the mark shown while the app has nothing else to show (SSO exchange, redirect).
 *
 * Menteo's mark is drawn inline so the two arches can be animated separately: they trace in as a
 * stroke, fill, then take turns hopping on a coral glow. The whitebrand tenants (Houdini, Virtual
 * Hub) ship their logos as opaque files, so they get the same glow with a breathing pulse instead.
 * Both variants freeze under `prefers-reduced-motion` (see globals.css).
 */

import { ThemeType, useBrandName, useLogoPath, useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';

interface BrandLoaderProps {
  /** Copy under the mark, already translated. */
  label: string;
  className?: string;
}

// Original menteo-logo.svg paths, untouched so the mark stays the mark.
const MENTEO_VIEWBOX = '0 0 1248.74 959.64';
const LEFT_ARCH =
  'M556.59,285.44c-57.78-53.23-144.79-87.17-242.16-87.17C148.52,198.27,12.61,296.72,.68,421.4c-.68,6.13-.68,11.92-.68,18.05V959.64H556.59V285.44Z';
const RIGHT_ARCH =
  'M1248.74,240.85V959.64H619.55V240.85c0-5.11,.33-10.56,.68-15.68C630.44,99.47,767.06,0,933.98,0s303.87,99.47,314.43,225.17c.33,5.11,.33,10.56,.33,15.68Z';

function MenteoMark() {
  const brandName = useBrandName();
  return (
    <svg
      viewBox={MENTEO_VIEWBOX}
      className="brand-loader-mark size-12 overflow-visible text-primary"
      role="img"
      aria-label={brandName}
    >
      <path d={LEFT_ARCH} pathLength={1} className="brand-loader-arch" style={{ animationDelay: '0s, 0s, 1.35s' }} />
      <path
        d={RIGHT_ARCH}
        pathLength={1}
        className="brand-loader-arch"
        style={{ animationDelay: '0.18s, 0.18s, 1.55s' }}
      />
    </svg>
  );
}

function TenantMark() {
  const logoPath = useLogoPath();
  return <img src={logoPath} alt="" className="brand-loader-pulse size-12 rounded-xl object-contain" />;
}

export function BrandLoader({ label, className }: BrandLoaderProps) {
  const { theme } = useTheme();
  const isMenteo = theme === ThemeType.MENTEO_THEME;

  return (
    <div
      className={cn('flex flex-col items-center gap-5 text-center', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="relative flex size-20 items-center justify-center">
        {/* Glow: sits under the mark and breathes in step with the hop. */}
        <div
          aria-hidden="true"
          className="brand-loader-glow absolute inset-1 rounded-full bg-primary/25 blur-xl dark:bg-primary/30"
        />
        <div className="relative flex size-16 items-center justify-center rounded-2xl bg-card/80 ring-1 ring-border/60 shadow-sm backdrop-blur-sm">
          {isMenteo ? <MenteoMark /> : <TenantMark />}
        </div>
      </div>
      <p className="text-sm font-medium text-muted-foreground motion-safe:animate-fade-up">{label}</p>
    </div>
  );
}
